import React from 'react';
import { BarChart, ArrowDown, ArrowUp, ArrowRight } from 'lucide-react';
import SeoRankingChart from '../charts/SeoRankingChart';
import { CompetitorData } from '../../types/dataTypes';

interface SeoMetricsSectionProps {
  data: CompetitorData;
}

const SeoMetricsSection: React.FC<SeoMetricsSectionProps> = ({ data }) => {
  const metrics = [
    {
      name: 'Organic Keywords',
      value: data.seoMetrics.organicKeywords,
      change: 12.5,
      changeType: 'positive',
    },
    {
      name: 'Avg. Position',
      value: data.seoMetrics.avgPosition,
      change: -0.8,
      changeType: 'positive',
    },
    {
      name: 'Traffic Value',
      value: `$${data.seoMetrics.trafficValue.toLocaleString()}`,
      change: 3.2,
      changeType: 'positive',
    },
    {
      name: 'Backlinks',
      value: data.seoMetrics.backlinks.toLocaleString(),
      change: 0,
      changeType: 'neutral',
    },
  ];

  const getChangeIcon = (type: string) => {
    if (type === 'positive') return <ArrowUp size={16} className="text-green-500" />;
    if (type === 'negative') return <ArrowDown size={16} className="text-red-500" />;
    return <ArrowRight size={16} className="text-gray-400" />;
  };

  const getChangeColor = (type: string) => {
    if (type === 'positive') return 'text-green-500';
    if (type === 'negative') return 'text-red-500';
    return 'text-gray-400';
  };

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          SEO Performance Metrics
        </h2>
        <div className="flex space-x-2">
          <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm leading-5 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200">
            <BarChart size={16} className="mr-1.5" />
            Compare Metrics
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {metrics.map((metric) => (
          <div 
            key={metric.name}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-100 dark:border-gray-700 transition-all duration-300 hover:shadow-md"
          >
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {metric.name}
            </p>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {metric.value}
            </p>
            <div className="mt-1 flex items-center">
              {getChangeIcon(metric.changeType)}
              <span className={`text-sm font-medium ml-1 ${getChangeColor(metric.changeType)}`}>
                {Math.abs(metric.change)}%
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                vs prev period
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-all duration-300 transform hover:shadow-lg">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Keyword Ranking Distribution
          </h3>
          <SeoRankingChart data={data} />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-all duration-300 transform hover:shadow-lg">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Top Organic Keywords
          </h3>
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Keyword</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Volume</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Difficulty</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {data.seoMetrics.topKeywords.map((keyword, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{keyword.term}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{keyword.position}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{keyword.volume}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full" 
                          style={{ width: `${keyword.difficulty}%` }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeoMetricsSection;